import { ol_Feature, ol_FeatureLike, ol_Map, ol_MapBrowserEvent, ol_source, ol_style, ol_layer } from "@omnia/exports/openlayers"
import { isFunction } from "../utils/utils"
import { Layer } from "./layer"
import { getSelectedStrokeStyle } from "./styles/defaults"
import { IMapSelectInteraction } from "./types"
import VectorSource from "ol/source/Vector"

type Options = {
    noText?: boolean
    noFillColor?: boolean
    ignoreLayerStyle?: boolean
}

export class MapSelectInteraction3 implements IMapSelectInteraction {

    private _overlay: ol_layer.Vector
    private _defaultStyles: ol_style.Style[]

    private selectableLayers: Layer[] = []

    protected _active: boolean = true

    private features: any[] = []

    private _styleProvider: (feature: ol_FeatureLike, resolution: number) => ol_style.Style[]

    private _watchers: ((selectedFeatures: any[]) => void)[] = []

    private _multiselect: boolean


    constructor(
        olmap: ol_Map,
        private readonly canSelectFeaturePredicate?: (feature: ol_FeatureLike) => boolean,
        private readonly options: Options = {}) {

        this.setup(olmap)

    }

    dispose() {
        // NOP
    }

    setup(olmap: ol_Map) {

        let strokeStyle = getSelectedStrokeStyle()

        this._defaultStyles = [strokeStyle.outer, strokeStyle.inner]

        this._overlay = new ol_layer.Vector({

            source: new ol_source.Vector<ol_FeatureLike>({
                useSpatialIndex: false
            }),

            updateWhileAnimating: true,
            updateWhileInteracting: true,

            style: (feature, resolution) => this.getStyle(feature, resolution)

        })

        this._overlay.setMap(olmap)

        olmap.on("click", e => {

            if (this._active) {
                this.handleClickEvent(e)
            }

        })

    }

    protected handleClickEvent(e: ol_MapBrowserEvent) {

        let found = this.selectClickTarget(e)

        if (!found) {
            this.selectFeaturesInternal([])
        }

    }

    protected selectClickTarget(e: ol_MapBrowserEvent): ol_Feature {

        let feature = this.findClickTarget(e)

        if (feature) {
            if (this._multiselect) {
                this.toggleFeatureSelection(feature)
            }
            else {
                this.selectFeatures([feature])
            }
        }

        return feature

    }

    protected findClickTarget(e: ol_MapBrowserEvent): ol_Feature {

        let map = e.map

        let pixel = map.getEventPixel(e["originalEvent"])

        let found: ol_Feature = <any>map.forEachFeatureAtPixel(pixel, (feature, olayer) => {

            let selectable = this.checkSelectableLayer(olayer)

            if (olayer && selectable) {

                if (this.canSelectFeature(feature)) {

                    return feature

                }

            }


        })

        return found

    }

    private canSelectFeature(feature: ol_FeatureLike) {

        if (this.canSelectFeaturePredicate) {
            return this.canSelectFeaturePredicate(feature)
        }

        return true

    }

    clearSelection() {

        this.selectFeaturesInternal([])

    }

    selectFeatures(selection: any[]) {

        var features = this.features

        if (features.length == selection.length) {

            if (selection.every((item, index) => features[index] == item)) {
                return
            }

        }

        this.selectFeaturesInternal(selection)

    }


    toggleFeatureSelection(feature: any) {

        var index = this.features.indexOf(feature)

        if (index < 0) {
            this.selectFeaturesInternal(this.features.concat([feature]))
        } else {
            var features = this.features.slice()
            features.splice(index, 1)
            this.selectFeaturesInternal(features)
        }

    }

    selectFeature(feature) {

        this.selectFeatures([feature])

    }


    private selectFeaturesInternal(features: ol_Feature[]) {

        this.features = (features || []).map(f => f.clone())

        let source = this._overlay.getSource()

        source.clear()
        source.addFeatures(this.features)

        this.notifySelectionChanged()

    }


    refresh() {

        var features = this.features.slice()

        this.selectFeatures(features)

    }

    setLayers(layers: Layer[], resetSelection: boolean = true) {

        if (resetSelection) {
            this.clearSelection()
        }

        this.selectableLayers = layers || []

    }

    addLayer(layer: Layer) {

        if (!this.selectableLayers.find(l => l == layer)) {
            this.selectableLayers.push(layer)
        }

    }

    removeLayer(layer: Layer) {

        this.selectableLayers.remove(l => l == layer)

    }

    private checkSelectableLayer(layer: any) {

        layer = this.selectableLayers.find((l) => l.mapLayer == layer)

        let selectable = layer ? layer.selectable : false

        return selectable

    }

    getStyle(feature: ol_FeatureLike, resolution: number) {

        let styles = this._defaultStyles

        if (!this.options.ignoreLayerStyle) {
            if (isFunction(this._styleProvider)) {
                styles = this._styleProvider(feature, resolution) || styles
            }
        }

        if (this.options.noText) {
            styles = styles.filter(s => !(s instanceof ol_style.Text))
        }

        if (this.options.noFillColor) {

            styles = styles.map(s => {
                s = (<any>s).clone()
                const fill = s.getFill()
                if (fill) {
                    fill.setColor("transparent")
                }
                return s
            })

        }

        return styles

    }

    setStyleProvider(provider: (feature: ol_Feature, resolution: number) => ol_style.Style[]) {
        this._styleProvider = provider
    }

    get active(): boolean {
        return this._active
    }

    set active(active) {
        this._active = active
    }

    notifySelectionChanged() {

        var selection = this.features.slice()

        this._watchers.forEach(w => w(selection))

    }

    watchSelection(watcher: (selectedFeatures: any[]) => void) {

        if (!this._watchers.find(w => w == watcher)) {
            this._watchers.push(watcher)
        }

    }

    unwatchSelection(watcher: (selectedFeatures: any[]) => void) {
        this._watchers.remove(w => w == watcher)
    }

    get enableMultipleSelection() {
        return this._multiselect
    }

    set enableMultipleSelection(value: boolean) {
        this._multiselect = value
    }

    public getSelectedFeatures(): any[] {
        return this.features.slice()
    }

    public getSelectedFeaturesCount(): number {
        return this.features.length
    }

    public refreshStyle() {
        this._overlay.setStyle(null)
        this._overlay.setStyle((feature, resolution) => this.getStyle(feature, resolution))
    }

}
